import React from "react";
import { ExamQuestion, ExamAnswerValue, ExamAttemptAnswer } from "@/types/exams";
import { CircleCheck, CircleX, CircleHelp, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";

interface ExamResultsViewProps {
  questions: ExamQuestion[];
  attemptAnswers: ExamAttemptAnswer[];
  score: number;
  totalCorrect: number;
  totalQuestions: number;
  onAskChat?: (questionIndex: number, questionText: string) => void;
}

export const ExamResultsView: React.FC<ExamResultsViewProps> = ({
  questions,
  attemptAnswers,
  score,
  totalCorrect,
  totalQuestions,
  onAskChat,
}) => {
  const safeQuestions = questions || [];
  const safeAttemptAnswers = attemptAnswers || [];

  const answerMap = new Map(
    safeAttemptAnswers.map((a) => [a.question_id, a])
  );

  const getAnswerStatus = (questionId: string) => {
    const attemptAnswer = answerMap.get(questionId);
    if (!attemptAnswer) return "skipped";

    const userAnswer = attemptAnswer.user_answer;
    const isSkipped =
      userAnswer === null ||
      userAnswer === undefined ||
      userAnswer === "" ||
      userAnswer === "__SKIPPED__" ||
      (Array.isArray(userAnswer) && userAnswer.length === 0);

    if (isSkipped) {
      return "skipped";
    }

    const answerScore = attemptAnswer.score;
    if (answerScore !== null && answerScore !== undefined && answerScore >= 0.5 && answerScore < 1 && !attemptAnswer.is_correct) {
      return "partial";
    }

    return attemptAnswer.is_correct ? "correct" : "incorrect";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "correct":
        return <CircleCheck className="h-5 w-5 text-correct" />;
      case "partial":
        return <CircleCheck className="h-5 w-5 text-warning" />;
      case "incorrect":
        return <CircleX className="h-5 w-5 text-incorrect" />;
      default:
        return <CircleHelp className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "correct":
        return "Correct";
      case "partial":
        return "Partial Credit";
      case "incorrect":
        return "Incorrect";
      default:
        return "Skipped";
    }
  };

  const getStatusBgClass = (status: string) => {
    switch (status) {
      case "correct":
        return "bg-correct/10";
      case "partial":
        return "bg-warning/10";
      case "incorrect":
        return "bg-incorrect/10";
      default:
        return "bg-muted";
    }
  };

  const getStatusTextClass = (status: string) => {
    switch (status) {
      case "correct":
        return "text-correct";
      case "partial":
        return "text-warning";
      case "incorrect":
        return "text-incorrect";
      default:
        return "text-muted-foreground";
    }
  };

  const getSourceBadgeClasses = (status: string) => {
    switch (status) {
      case "correct":
        return "bg-correct/10 border-correct/10 text-correct";
      case "partial":
        return "bg-warning/10 border-warning/10 text-warning";
      case "incorrect":
        return "bg-incorrect/10 border-incorrect/10 text-incorrect";
      default:
        return "bg-muted border-foreground-muted text-muted-foreground";
    }
  };

  const formatUserAnswerText = (question: ExamQuestion, userAnswer: ExamAnswerValue) => {
    if (
      userAnswer === null ||
      userAnswer === "" ||
      userAnswer === "__SKIPPED__" ||
      (Array.isArray(userAnswer) && userAnswer.length === 0)
    ) {
      return null;
    }
    return String(userAnswer);
  };

  return (
    <div className="flex flex-col gap-16">
      {safeQuestions.map((question, idx) => {
        const status = getAnswerStatus(question.id);
        const attemptAnswer = answerMap.get(question.id);
        const userAnswer = attemptAnswer?.user_answer ?? null;
        const explanation = attemptAnswer?.explanation;
        const sourceReference = question.content.source_reference;
        const isShortAnswer = question.type === "short-answer" || question.type === "fill-blank";

        return (
          <div key={question.id} className="p-2">
            {/* Question Header */}
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="text-md flex flex-1 space-x-2 font-normal leading-relaxed">
                <span className="flex-shrink-0">{idx + 1}.</span>
                <div className="flex-1">
                  <div className="prose prose-neutral dark:prose-invert max-w-none">
                    <p className="text-base leading-7 mb-0">{question.content.prompt}</p>
                  </div>
                </div>
              </div>
              {onAskChat && (
                <div className="items-end">
                  <Button
                    variant="outline"
                    className="h-9 rounded-lg px-3 gap-x-2"
                    onClick={() => onAskChat(idx, question.content.prompt)}
                  >
                    <span>Ask chat</span>
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>

            {/* MCQ / True-False Options */}
            {(question.type === "multiple-choice" || question.type === "true-false") && (
              <div className="space-y-2">
                {(question.type === "true-false"
                  ? ["True", "False"]
                  : question.content.choices || []
                ).map((choice, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const isUserAnswer = userAnswer === letter;
                  const correctAnswer = question.content.answer;
                  
                  // Handle True/False questions where answer might be "True"/"False" or "A"/"B"
                  let isCorrectAnswer = false;
                  if (question.type === "true-false") {
                    const normalizedCorrect = correctAnswer?.toString().toLowerCase();
                    const isCorrectTrue = normalizedCorrect === 'true' || normalizedCorrect === 'a';
                    const isCorrectFalse = normalizedCorrect === 'false' || normalizedCorrect === 'b';
                    
                    if (letter === 'A') {
                      isCorrectAnswer = isCorrectTrue;
                    } else if (letter === 'B') {
                      isCorrectAnswer = isCorrectFalse;
                    }
                  } else {
                    isCorrectAnswer = Array.isArray(correctAnswer)
                      ? correctAnswer.includes(letter)
                      : correctAnswer === letter;
                  }

                  // Determine border styling based on answer state
                  let borderClass = "border-[1.5px]";
                  if (isUserAnswer && isCorrectAnswer) {
                    // User selected correct answer - solid green
                    borderClass = "border-[1.5px] border-correct";
                  } else if (isUserAnswer && !isCorrectAnswer) {
                    // User selected incorrect answer - solid red
                    borderClass = "border-[1.5px] border-incorrect";
                  } else if (isCorrectAnswer && !isUserAnswer) {
                    // Correct answer that user didn't select - dashed green
                    borderClass = "border-[1.5px] border-dashed border-correct";
                  }

                  return (
                    <button
                      key={i}
                      disabled
                      className={`w-full rounded-2xl p-2.5 text-left text-primary/90 transition-all flex items-start gap-2 hover:bg-accent/50 cursor-default ${borderClass}`}
                    >
                      <span className="ml-1 pt-1 text-sm font-semibold text-muted-foreground">
                        {letter}.
                      </span>
                      <div className="prose prose-neutral dark:prose-invert max-w-none">
                        <div className="space-y-4 flex-1">
                          <p className="text-base leading-7 last:mb-0">{choice}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Short Answer Textarea */}
            {isShortAnswer && (
              <div className="flex flex-col gap-2">
                <textarea
                  disabled
                  placeholder="Type your answer here..."
                  value={formatUserAnswerText(question, userAnswer) || ""}
                  className="w-full min-h-[120px] rounded-2xl border-[1.5px] border-neutral-200 dark:border-neutral-700 bg-background px-4 py-3 text-base resize-none disabled:opacity-70 disabled:cursor-not-allowed"
                />
              </div>
            )}

            {/* Feedback Box */}
            <div className={`mt-4 rounded-lg p-4 ${getStatusBgClass(status)}`}>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <h3 className={`flex items-center gap-2 font-medium ${getStatusTextClass(status)}`}>
                    {getStatusIcon(status)}
                    {getStatusText(status)}
                  </h3>
                </div>

                {/* Score for short answer */}
                {isShortAnswer && attemptAnswer?.score !== null && attemptAnswer?.score !== undefined && (
                  <p className={`text-sm mt-2 ${getStatusTextClass(status)}`}>
                    Score: {Math.round((attemptAnswer.score || 0) * 4)}/4
                  </p>
                )}

                {/* Explanation */}
                {explanation && (
                  <div className={`mt-2 text-sm font-normal leading-relaxed ${getStatusTextClass(status)}`}>
                    <div className="prose prose-neutral dark:prose-invert max-w-none">
                      <div className="space-y-4 text-sm leading-normal">
                        <ReactMarkdown
                          components={{
                            strong: ({ children }) => (
                              <strong className="font-semibold">{children}</strong>
                            ),
                            p: ({ children }) => <p className="text-base leading-7 last:mb-0">{children}</p>,
                          }}
                        >
                          {explanation}
                        </ReactMarkdown>
                      </div>
                    </div>
                    {/* Source Reference Badge */}
                    {sourceReference && (
                      <span className="items-center">
                        <span className={`inline-flex items-center border px-2.5 py-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 mt-2 cursor-pointer space-x-2 rounded-sm text-xs font-medium ${getSourceBadgeClasses(status)}`}>
                          {sourceReference.includes(':') ? (
                            <>
                              <span>{sourceReference.split(':')[0].trim()}</span>
                              <span>:</span>
                              <span className="max-w-[10rem] truncate">{sourceReference.split(':').slice(1).join(':').trim()}</span>
                            </>
                          ) : (
                            <span>{sourceReference}</span>
                          )}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
